import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { EventBus } from '../core/EventBus';
import { Mat4 } from '../math/Mat4';
import { worldToAxial } from '../math/HexMath';

const CLICK_THRESHOLD_SQ = 64;

/**
 * Converts screen clicks to hex coordinates.
 * Always emits hex:clicked for general use.
 * Emits travel:hex-clicked only when enabled (Travel-Modus).
 * Uses ray-plane intersection with y=0 via inverse view-projection.
 */
export class HexPicker implements GameModule {
  readonly name = 'HexPicker';

  private _camera: Camera;
  private _hexRadius: number;
  private _events!: EventBus;
  private _canvas!: HTMLCanvasElement;
  private _enabled = false;

  private _downX = 0;
  private _downY = 0;
  private _isDown = false;

  private readonly _invVP = new Mat4();

  constructor(camera: Camera, hexRadius: number) {
    this._camera = camera;
    this._hexRadius = hexRadius;
  }

  get enabled(): boolean { return this._enabled; }
  set enabled(v: boolean) { this._enabled = v; }

  init(engine: Engine): void {
    this._events = engine.events;
    this._canvas = engine.canvas;
    this._canvas.addEventListener('pointerdown', this._onDown);
    this._canvas.addEventListener('pointerup', this._onUp);
  }

  update(_dt: number): void {}

  dispose(): void {
    this._canvas.removeEventListener('pointerdown', this._onDown);
    this._canvas.removeEventListener('pointerup', this._onUp);
  }

  private _onDown = (e: PointerEvent): void => {
    this._isDown = true;
    this._downX = e.clientX;
    this._downY = e.clientY;
  };

  private _onUp = (e: PointerEvent): void => {
    if (!this._isDown) { return; }
    this._isDown = false;

    const dx = e.clientX - this._downX;
    const dy = e.clientY - this._downY;
    if (dx * dx + dy * dy > CLICK_THRESHOLD_SQ) return;

    const hit = this._screenToGround(e.clientX, e.clientY);
    if (!hit) return;

    const axial = worldToAxial(hit[0], hit[1], this._hexRadius);
    this._events.emit('hex:clicked', { q: axial.q, r: axial.r, screenX: e.clientX, screenY: e.clientY });
    if (this._enabled) {
      this._events.emit('travel:hex-clicked', { q: axial.q, r: axial.r });
    }
  };

  private _screenToGround(px: number, py: number): [number, number] | null {
    const rect = this._canvas.getBoundingClientRect();
    const ndcX = ((px - rect.left) / rect.width) * 2 - 1;
    const ndcY = 1 - ((py - rect.top) / rect.height) * 2;

    const vp = this._invVP;
    vp.data.set(this._camera.projection.data);
    vp.multiply(this._camera.view);
    vp.invert();

    const d = vp.data;
    const nw = d[3] * ndcX + d[7] * ndcY + d[11] * -1 + d[15];
    const nx = (d[0] * ndcX + d[4] * ndcY + d[8] * -1 + d[12]) / nw;
    const ny = (d[1] * ndcX + d[5] * ndcY + d[9] * -1 + d[13]) / nw;
    const nz = (d[2] * ndcX + d[6] * ndcY + d[10] * -1 + d[14]) / nw;

    const fw = d[3] * ndcX + d[7] * ndcY + d[11] + d[15];
    const fx = (d[0] * ndcX + d[4] * ndcY + d[8] + d[12]) / fw;
    const fy = (d[1] * ndcX + d[5] * ndcY + d[9] + d[13]) / fw;
    const fz = (d[2] * ndcX + d[6] * ndcY + d[10] + d[14]) / fw;

    const dirY = fy - ny;
    if (Math.abs(dirY) < 1e-8) return null;
    const t = -ny / dirY;
    if (t < 0) return null;
    return [nx + (fx - nx) * t, nz + (fz - nz) * t];
  }
}
